import React, { useRef, useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ImageCropperProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedImage: string | null;
  onCrop: (blob: Blob) => Promise<void>;
  uploading: boolean;
  cropAspectRatio?: number;
  dialogTitle?: string;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  open,
  onOpenChange,
  selectedImage,
  onCrop,
  uploading,
  cropAspectRatio = 1,
  dialogTitle = 'Crop Image',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const drawImage = () => {
    if (selectedImage && imageRef.current && canvasRef.current) {
      const img = imageRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      const imageAspectRatio = img.width / img.height;
      let cropWidth = img.width;
      let cropHeight = img.height;

      if (imageAspectRatio > cropAspectRatio) {
        cropHeight = img.height;
        cropWidth = cropHeight * cropAspectRatio;
      } else {
        cropWidth = img.width;
        cropHeight = cropWidth / cropAspectRatio;
      }

      canvas.width = cropWidth;
      canvas.height = cropHeight;
      
      // Calculate the maximum offset to keep the image within bounds
      const maxOffsetX = (img.width - cropWidth) / 2;
      const maxOffsetY = (img.height - cropHeight) / 2;
      
      // Clamp the offset values
      const clampedOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, offsetX));
      const clampedOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, offsetY));
      
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      ctx?.drawImage(
        img,
        (img.width - cropWidth) / 2 + clampedOffsetX,
        (img.height - cropHeight) / 2 + clampedOffsetY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );
    }
  };

  useEffect(() => {
    drawImage();
  }, [selectedImage, offsetX, offsetY]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - offsetX, y: e.clientY - offsetY });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDragging && imageRef.current && canvasRef.current) {
      const newOffsetX = e.clientX - dragStart.x;
      const newOffsetY = e.clientY - dragStart.y;
      setOffsetX(newOffsetX);
      setOffsetY(newOffsetY);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleCrop = async () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const blob = await new Promise<Blob>((resolve) => 
      canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.9)
    );
    
    await onCrop(blob);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          {selectedImage && (
            <>
              <img
                ref={imageRef}
                src={selectedImage}
                alt="Selected"
                className="hidden"
                onLoad={() => drawImage()}
              />
              <div className="relative">
                <canvas
                  ref={canvasRef}
                  className="max-w-full border rounded-lg cursor-move"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
              </div>
              <Button onClick={handleCrop} disabled={uploading} className="w-full">
                {uploading ? 'Uploading...' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropper;
